import React, { useRef, useState } from "react";

import {
  bankAccounts,
  getRecentTransactions,
  getTransactionsForAccount,
} from "~/data";
import AccountSummaryCard from "~/pages/SuperAdminDashboard/AccountSummaryCard";
import TransactionsCard from "~/pages/SuperAdminDashboard/TransactionsCard";

// Animated scroll with a controllable duration (native smooth scroll is too fast
// and its speed is not configurable)
const smoothScrollTo = (element: HTMLElement, duration = 900) => {
  const findScrollParent = (node: HTMLElement): HTMLElement | null => {
    let current = node.parentElement;
    while (current) {
      const { overflowY } = window.getComputedStyle(current);
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  const container = findScrollParent(element);
  const containerTop = container ? container.getBoundingClientRect().top : 0;
  const start = container ? container.scrollTop : window.scrollY;
  const headroom = 16; // matches scroll-mt-4
  const target =
    start + element.getBoundingClientRect().top - containerTop - headroom;
  const startTime = performance.now();

  const easeInOutCubic = (t: number) =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / duration, 1);
    const y = start + (target - start) * easeInOutCubic(progress);
    if (container) {
      container.scrollTop = y;
    } else {
      window.scrollTo(0, y);
    }
    if (progress < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
};

const OverviewTab: React.FC = () => {
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState("");
  const transactionsRef = useRef<HTMLDivElement>(null);

  const recentTransactions = selectedAccountId
    ? getTransactionsForAccount(selectedAccountId)
    : getRecentTransactions(10);

  const filteredTransactions = recentTransactions.filter(
    (transaction) =>
      transaction.description
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.reference.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAccountSelect = (accountId: string) => {
    const isSelecting = selectedAccountId !== accountId;
    setSelectedAccountId((prev) => (prev === accountId ? null : accountId));

    // Scroll down to the transactions table when scoping to an account
    if (isSelecting) {
      requestAnimationFrame(() => {
        if (transactionsRef.current) {
          smoothScrollTo(transactionsRef.current);
        }
      });
    }
  };

  const selectedAccountName = selectedAccountId
    ? bankAccounts.find((a) => a.id === selectedAccountId)?.bankName
    : null;

  return (
    <div className="w-full min-w-0">
      <div className="space-y-4 sm:space-y-6">
        <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <AccountSummaryCard
            showDetailedView={showDetailedView}
            setShowDetailedView={setShowDetailedView}
            selectedAccountId={selectedAccountId}
            handleAccountSelect={handleAccountSelect}
          />
        </div>
        <div
          ref={transactionsRef}
          className="scroll-mt-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        >
          <TransactionsCard
            selectedAccountId={selectedAccountId}
            setSelectedAccountId={setSelectedAccountId}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            bankName={selectedAccountName || undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
